import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { getInitials } from "../lib/auth";
import { useRoles } from "../lib/useRoles";
import { useTechIdentity } from "../lib/TechIdentityContext";
import ViewAsUser from "./ViewAsUser";
import NeuroGenLogo from "./NeuroGenLogo";

const NavIcons = {
  dashboard: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="6" height="6" rx="1"/>
      <rect x="9" y="1" width="6" height="6" rx="1"/>
      <rect x="1" y="9" width="6" height="6" rx="1"/>
      <rect x="9" y="9" width="6" height="6" rx="1"/>
    </svg>
  ),
  documents: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/>
      <path d="M9 1v4h4"/>
      <line x1="5" y1="8" x2="11" y2="8"/>
      <line x1="5" y1="11" x2="11" y2="11"/>
    </svg>
  ),
  mso: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2.5" width="12" height="11" rx="1"/>
      <path d="M5 6h6M5 8.5h6" strokeLinecap="round"/>
      <path d="M5.5 11.5l1.25 1.25L9.5 10" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  idr: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/>
      <path d="M9 1v4h4"/>
      <path d="M5.5 9l1.5 1.5L10 7.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  upload: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 10V3"/>
      <polyline points="5 6 8 3 11 6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"/>
    </svg>
  ),
  batch: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 10V3"/>
      <polyline points="5 6 8 3 11 6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"/>
      <line x1="4" y1="9" x2="2" y2="9"/>
      <line x1="14" y1="9" x2="12" y2="9"/>
    </svg>
  ),
  agent: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 8c0 3.314-2.686 6-6 6a5.99 5.99 0 01-3-0.8L1 14l0.8-4A6 6 0 1114 8z"/>
      <circle cx="5.5" cy="8" r="0.75" fill="currentColor" stroke="none"/>
      <circle cx="8"   cy="8" r="0.75" fill="currentColor" stroke="none"/>
      <circle cx="10.5" cy="8" r="0.75" fill="currentColor" stroke="none"/>
    </svg>
  ),
  guide: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6"/>
      <path d="M6.5 6a1.5 1.5 0 013 0c0 1.5-1.5 1.5-1.5 3"/>
      <circle cx="8" cy="12" r=".5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  audit: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M13 2H3a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V3a1 1 0 00-1-1z"/>
      <line x1="5" y1="5" x2="11" y2="5"/>
      <line x1="5" y1="8" x2="11" y2="8"/>
      <line x1="5" y1="11" x2="8"  y2="11"/>
    </svg>
  ),
  ipReporting: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="2" y1="14" x2="14" y2="14" strokeLinecap="round"/>
      <rect x="3" y="9" width="2" height="5" fill="currentColor" stroke="none"/>
      <rect x="7" y="5" width="2" height="9" fill="currentColor" stroke="none"/>
      <rect x="11" y="2" width="2" height="12" fill="currentColor" stroke="none"/>
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
      style={{ width: 14, height: 14, color: "var(--text-on-dark-dim)" }}>
      <path d="M10 2h3a1 1 0 011 1v10a1 1 0 01-1 1h-3"/>
      <polyline points="7 11 10 8 7 5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="10" y1="8" x2="2" y2="8"/>
    </svg>
  ),
};

const CollapseIcon = ({ collapsed }) => (
  <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round"
       style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
    <path d="M10 4L6 8l4 4" />
    <line x1="13" y1="3" x2="13" y2="13" />
  </svg>
);

const COLLAPSE_KEY = "qaportal.sidebarCollapsed";

// Narrow enough that the rail costs more than the labels are worth, so it
// starts closed. This is read once, on mount: resizing never moves the
// sidebar, because shutting it under someone mid-task to reclaim 174px is a
// worse outcome than a wide sidebar in a narrow window they can close
// themselves.
const NARROW = "(max-width: 1100px)";

const readPref = () => {
  try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
};

export default function Sidebar({ user, onLogout, failedCount = 0 }) {
  const { isAdmin, isTech, isSuperAdmin, isIP } = useRoles();
  // Narrow wins at load; above the breakpoint the stored choice applies. After
  // that only the toggle moves it.
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.matchMedia(NARROW).matches || readPref(); } catch { return readPref(); }
  });

  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* not critical */ }
      return next;
    });
  };
  // An admin who also runs cases has a tech record, so they get the link to
  // their own report too - the monthly email points there either way.
  const { techWebId } = useTechIdentity();
  const initials     = getInitials(user?.name || user?.username || "QA");
  const displayName  = user?.name || user?.username || "QA Reviewer";

  const navLink = (to, icon, label, badge = null) => (
    <NavLink to={to} title={collapsed ? label : undefined}
             className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
      {icon}
      <span className="nav-label">{label}</span>
      {badge}
    </NavLink>
  );

  return (
    <aside className={`sidebar${collapsed ? " collapsed" : ""}`}>

      {/* Brand */}
      <div className="sidebar-brand">
        {collapsed
          ? <NeuroGenLogo size={22} variant="light" iconOnly />
          : <NeuroGenLogo size={38} variant="light" subtitle="QA PORTAL" />}
        <button type="button" className="sidebar-toggle"
                onClick={toggle}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-expanded={!collapsed}
                title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <CollapseIcon collapsed={collapsed} />
        </button>
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">

        <span className="nav-group-label" aria-hidden={collapsed}>Overview</span>
        {navLink("/dashboard", NavIcons.dashboard, "Dashboard")}

        <span className="nav-group-label" aria-hidden={collapsed}>Cases</span>
        {navLink(
          "/documents",
          NavIcons.documents,
          "All Cases",
          failedCount > 0 && <span className="nav-badge">{failedCount}</span>
        )}
        {((isTech && !isAdmin) || techWebId) && navLink("/my-report", NavIcons.audit, "My Monthly Report")}
        {isIP && navLink("/my-ip-report", NavIcons.ipReporting, "My IP Report")}
        {isAdmin && navLink("/mso", NavIcons.mso, "Manager Sign Off")}
        {isAdmin && navLink("/idr", NavIcons.idr, "IDR")}
        {isAdmin && navLink("/upload", NavIcons.upload, "Upload")}

        {isAdmin && (
          <>

        <span className="nav-group-label" aria-hidden={collapsed}>Intelligence</span>
        {navLink("/agent", NavIcons.agent, "AI Assistant")}

        <span className="nav-group-label" aria-hidden={collapsed}>Reporting</span>
        {navLink("/audit", NavIcons.audit, "NP Reports")}
        {navLink("/ip-reporting", NavIcons.ipReporting, "IP Reports")}
        
        <span className="nav-group-label" aria-hidden={collapsed}>Help</span>
        {navLink("/guide", NavIcons.guide, "User Guide")}

        
            <span className="nav-group-label" aria-hidden={collapsed}>Admin</span>
            {navLink("/settings/qa-checks",
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M12 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V3a1 1 0 00-1-1z"/>
                <path d="M6 5h4M6 8h4M6 11h2"/>
              </svg>,
              "QA Checks"
            )}
            {navLink("/settings/idr-prompt",
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M9.5 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V5.5z"/>
                <path d="M9.5 2v3.5H13M5.5 9h5M5.5 11.5h3"/>
              </svg>,
              "IDR Wording"
            )}
            {isSuperAdmin && navLink("/admin/users",
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="6" cy="5.5" r="2.5"/><path d="M1.5 13.5c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4"/>
                <path d="M11 7.5a2 2 0 100-4M12.5 13.5c0-1.8-.8-3-2-3.6"/>
              </svg>,
              "Users"
            )}
          </>
        )}

      </nav>

      {/* User */}
      <div className="sidebar-user">
        <ViewAsUser collapsed={collapsed} />
        <div className="user-tile" onClick={onLogout}
             title={collapsed ? `${displayName} - sign out` : "Sign out"}>
          <div className="user-avatar">{initials}</div>
          <div className="user-details">
            <div className="user-name">{displayName}</div>
            <div className="user-role">{isAdmin ? "Administrator" : isTech ? "Technologist" : isIP ? "Interpreting Physician" : "QA Reviewer"}</div>
          </div>
          {NavIcons.logout}
        </div>
      </div>

    </aside>
  );
}


